import {
	CapTableRow,
	StaticColumn,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/capTableTypes';
import { Column, Table } from '@tanstack/react-table';
import {
	CELL_MIN_HEIGHT,
	COLUMN_MIN_WIDTH,
	INSTRUMENT_COLUMN_WIDTH,
	TOTAL_SECURITIES_COLUMN_WIDTH,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableElements.tsx';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

const OVERSCAN = 10;

// https://tanstack.com/table/latest/docs/framework/react/examples/virtualized-columns-experimental
export const useCapTableVirtualizer = (
	table: Table<CapTableRow>,
	tableWrapperRef: React.RefObject<HTMLDivElement>
) => {
	const PADDING_END = COLUMN_MIN_WIDTH;

	const tableBodyRef = React.useRef<HTMLTableSectionElement>(null);
	const rowRefsMap = React.useRef<Map<number, HTMLTableRowElement>>(new Map());

	const { rows } = table.getRowModel();
	const visibleColumns = table.getVisibleLeafColumns();

	const handleRowVirtualizerChange = useCallback((instance) => {
		if (!tableBodyRef.current) {
			return;
		}
		tableBodyRef.current.style.height = `${instance.getTotalSize()}px`;
		instance.getVirtualItems().forEach((virtualRow) => {
			const rowRef = rowRefsMap.current.get(virtualRow.index);
			if (!rowRef) {
				return;
			}
			rowRef.style.transform = `translateY(${virtualRow.start}px)`;
		});
	}, []);

	const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
		count: rows.length,
		estimateSize: () => CELL_MIN_HEIGHT,
		getScrollElement: () => tableWrapperRef.current,
		overscan: OVERSCAN,
		onChange: handleRowVirtualizerChange,
	});

	const { staticColumns, virtualizableColumns } = useMemo(() => {
		const staticColumns: Column<CapTableRow, unknown>[] = [];
		const virtualizableColumns: Column<CapTableRow, unknown>[] = [];

		for (const visibleColumn of visibleColumns) {
			if (
				visibleColumn.id === StaticColumn.INSTRUMENT ||
				visibleColumn.id === StaticColumn.TOTAL_SECURITIES
			) {
				staticColumns.push(visibleColumn);
			} else {
				virtualizableColumns.push(visibleColumn);
			}
		}

		return { staticColumns, virtualizableColumns };
	}, [visibleColumns]);

	const tableHeaderRowRef = useRef<HTMLTableRowElement>(null);

	const columnVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableCellElement>({
		horizontal: true,
		count: virtualizableColumns.length,
		overscan: OVERSCAN,
		paddingEnd: PADDING_END,
		estimateSize: () => COLUMN_MIN_WIDTH,
		getScrollElement: () => tableWrapperRef.current,
		onChange: (instance) => {
			// Hack: Adds some PADDING_END to the right of the table and removes it near the end
			// This avoids a jump / snap back
			const virtualColumns = instance.getVirtualItems();
			const totalSize = instance.getTotalSize();
			const stickyColumnsWidth = INSTRUMENT_COLUMN_WIDTH + TOTAL_SECURITIES_COLUMN_WIDTH;
			const scrollOffset = instance.scrollOffset || 0;
			const visibleWidth = instance.scrollRect?.width || 0;
			const adjustedVisibleWidth = visibleWidth - stickyColumnsWidth;
			const remainingScrollRight =
				totalSize - (scrollOffset + adjustedVisibleWidth) - PADDING_END;
			const removePaddingTrigger = PADDING_END / 2;

			const virtualPaddingLeft = virtualColumns[0]?.start ?? 0;
			const virtualPaddingRight =
				instance.getTotalSize() - (virtualColumns[virtualColumns.length - 1]?.end ?? 0);
			const adjustedPaddingRight =
				remainingScrollRight <= removePaddingTrigger ? 0 : virtualPaddingRight;

			tableWrapperRef.current?.style.setProperty(
				'--cap-table-virtual-padding-left',
				`${virtualPaddingLeft}px`
			);
			tableWrapperRef.current?.style.setProperty(
				'--cap-table-virtual-padding-right',
				`${adjustedPaddingRight}px`
			);
		},
	});

	useLayoutEffect(() => {
		rowVirtualizer.measure();
		columnVirtualizer.measure();
	}, [rows.length, virtualizableColumns.length, tableBodyRef.current]);

	const virtualRowIndexes = rowVirtualizer.getVirtualIndexes();
	const virtualColumnIndexes = columnVirtualizer.getVirtualIndexes();

	return {
		rows,
		rowVirtualizer,
		columnVirtualizer,
		tableBodyRef,
		virtualRowIndexes,
		virtualColumnIndexes,
		rowRefsMap,
		staticColumns,
		virtualizableColumns,
		tableHeaderRowRef,
	};
};
